/**
 * Traduce contenido del menú cargado por el local (ingredientes, extras, etc.)
 * para la UI del cliente. No usar con el nombre del negocio.
 */
import i18n from '@/i18n';
import { MENU_CONTENT_GLOSSARY, MENU_CONNECTORS } from './menuContentGlossary';

function stripDiacritics(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Clave normalizada para lookup (minúsculas, sin tildes, espacios colapsados). */
export function normalizeMenuTerm(text) {
  return stripDiacritics(String(text || ''))
    .toLowerCase()
    .replace(/['’`]/g, '')
    .replace(/[^a-z0-9+/.\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function targetLang(lng) {
  const code = String(lng || i18n.language || 'es')
    .trim()
    .toLowerCase()
    .slice(0, 2);
  if (code === 'es' || !code) return 'es';
  return code;
}

function glossaryHit(normKey, lng) {
  if (!normKey || lng === 'es') return null;
  const entry = MENU_CONTENT_GLOSSARY[normKey];
  if (!entry) return null;
  const hit = entry[lng];
  return hit != null && String(hit).trim() ? String(hit) : null;
}

/**
 * Traduce un término o frase corta del menú (ingrediente, extra, opción).
 * Si no hay match, intenta traducir palabra por palabra.
 */
export function translateMenuTerm(text, lng) {
  const raw = text == null ? '' : String(text);
  if (!raw.trim()) return raw;
  const lang = targetLang(lng);
  if (lang === 'es') return raw;

  const exact = glossaryHit(normalizeMenuTerm(raw), lang);
  if (exact) {
    // Preservar mayúscula inicial del original si aplica
    if (/^[A-ZÁÉÍÓÚÑ]/.test(raw.trim()) && exact.length) {
      return exact.charAt(0).toUpperCase() + exact.slice(1);
    }
    return exact;
  }

  // Partes separadas por coma / slash / +
  if (/[,/+|]/.test(raw)) {
    const parts = raw.split(/(\s*[,/+|]\s*)/);
    let changed = false;
    const out = parts.map((part) => {
      if (/^\s*[,/+|]\s*$/.test(part)) return part;
      const tr = translateMenuTerm(part.trim(), lang);
      if (tr !== part.trim()) changed = true;
      // preserve leading/trailing spaces of part roughly
      const lead = part.match(/^\s*/)?.[0] || '';
      const trail = part.match(/\s*$/)?.[0] || '';
      return lead + tr + trail;
    });
    if (changed) return out.join('');
  }

  return translateMenuPhraseWords(raw, lang);
}

function translateMenuPhraseWords(text, lang) {
  const connectors = MENU_CONNECTORS[lang] || {};
  const tokens = text.split(/(\s+)/);
  let changed = false;
  const out = tokens.map((tok) => {
    if (!tok || /^\s+$/.test(tok)) return tok;
    const punctLead = tok.match(/^[^A-Za-zÁÉÍÓÚÑáéíóúñ0-9]+/)?.[0] || '';
    const punctTrail = tok.match(/[^A-Za-zÁÉÍÓÚÑáéíóúñ0-9]+$/)?.[0] || '';
    const core = tok.slice(punctLead.length, tok.length - punctTrail.length || undefined);
    if (!core) return tok;
    const norm = normalizeMenuTerm(core);
    let hit = glossaryHit(norm, lang);
    if (!hit && connectors[norm]) hit = connectors[norm];
    if (!hit) return tok;
    changed = true;
    let rendered = hit;
    if (/^[A-ZÁÉÍÓÚÑ]/.test(core) && rendered.length) {
      rendered = rendered.charAt(0).toUpperCase() + rendered.slice(1);
    }
    return punctLead + rendered + punctTrail;
  });
  return changed ? out.join('') : text;
}

export function translateMenuTermList(items, lng) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => translateMenuTerm(item, lng));
}

/** Idioma actual de i18n (atajo para componentes). */
export function currentMenuContentLang() {
  return targetLang(i18n.language);
}
